import { EMPLOYMENT_TYPE_BADGE, EMPLOYMENT_TYPE_LABELS } from '../types.js'
import { Fragment, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { AdminWorkerDetail } from './AdminWorkerDetail.js'
import type { AdminFacility } from '../../facilities/index.js'
import type { WorkerTableBidStatus, WorkerTableRow } from '../index.js'
import { TIER_BG_CLASS, TIER_LABELS, Skeleton } from '../../core/index.js'
import { OnTrackHint } from '../../scoring/index.js'
import { WORKER_ROLE_BADGE_CLASS, WORKER_ROLE_LABELS } from '../../core/index.js'
import { Modal } from '../../core/index.js'
import { WorkerIcon } from '../../core/index.js'
import { SortableTh, TIER_SORT_RANK, useSort, type SortAccessors } from '../../core/index.js'
import { Check } from 'lucide-react'
import { Button } from '../../core/index.js'
import type { ComponentProps } from 'react'
import type { RosterAdminApi } from '../adminApi.js'
import { LoadFailure } from '../../core/index.js'

/** The admin calls this screen makes itself, and what the screens it opens need. */
type AdminApi = RosterAdminApi & ComponentProps<typeof AdminWorkerDetail>['api']


const BID_STATUS_LABELS: Record<WorkerTableBidStatus, string> = {
  open: 'Open',
  upcoming: 'Upcoming',
  assigned: 'Assigned',
}

/** A metric the facility hasn't reported yet is a dash, never a 0 - the two mean different things. */
function metric(value: number | null, suffix = ''): string {
  return value === null ? '—' : `${value}${suffix}`
}

const WORKER_SORT: SortAccessors<WorkerTableRow, 'name' | 'role' | 'tier' | 'score' | 'callOuts' | 'charting' | 'bidStatus'> = {
  name: (worker) => worker.name,
  role: (worker) => worker.role,
  tier: (worker) => TIER_SORT_RANK[worker.tier],
  score: (worker) => worker.score,
  callOuts: (worker) => worker.callOuts,
  charting: (worker) => worker.chartingPct,
  bidStatus: (worker) => BID_STATUS_LABELS[worker.bidStatus],
}

function WorkersTable({ api, clientId, workers }: { api: AdminApi; clientId: string; workers: WorkerTableRow[] }) {
  const [openWorkerId, setOpenWorkerId] = useState<string | null>(null)
  const { sorted, header } = useSort(workers, WORKER_SORT)
  return (
    <table className="w-full text-left text-xs">
      <thead className="sticky top-0 bg-white">
        <tr className="border-b border-marine/15 text-[10px] uppercase tracking-wide text-ink-mid">
          <SortableTh className="px-4 py-2" label="Worker" {...header('name')} />
          <SortableTh className="px-3 py-2" label="Role" {...header('role')} />
          <SortableTh className="px-3 py-2" label="Tier" {...header('tier')} />
          <SortableTh className="px-3 py-2" label="Score" {...header('score')} alignRight />
          <SortableTh className="px-3 py-2" label="Call-outs" {...header('callOuts')} alignRight />
          <SortableTh className="px-3 py-2" label="Charting" {...header('charting')} alignRight />
          <SortableTh className="px-4 py-2" label="Bid status" {...header('bidStatus')} />
        </tr>
      </thead>
      <tbody>
        {sorted.map((worker) => (
          <Fragment key={worker.workerId}>
          <tr className="border-b border-marine/10 cursor-pointer hover:bg-canvas" onClick={() => setOpenWorkerId(openWorkerId === worker.workerId ? null : worker.workerId)}>
            <td className="px-4 py-2">
              <div className="flex items-start gap-2">
                <WorkerIcon className="mt-0.5 w-4 h-4 text-marine" />
                <div className="min-w-0">
              <button type="button" aria-expanded={openWorkerId === worker.workerId} className="font-bold text-ink text-left underline decoration-marine/30 underline-offset-2 hover:text-marine">
                {worker.name}
              </button>
                </div>
              </div>
            </td>
            <td className="px-3 py-2">
              <span className={`inline-block px-1.5 py-0.5 text-[10px] font-bold rounded ${WORKER_ROLE_BADGE_CLASS}`} title={WORKER_ROLE_LABELS[worker.role]}>
                {worker.role}
              </span>
              {EMPLOYMENT_TYPE_BADGE[worker.employmentType ?? 'full_time'] && (
                <span className="ml-1 inline-block px-1.5 py-0.5 text-[10px] font-bold rounded bg-gold/25 text-ink" title={EMPLOYMENT_TYPE_LABELS[worker.employmentType ?? 'full_time']}>
                  {EMPLOYMENT_TYPE_BADGE[worker.employmentType ?? 'full_time']}
                </span>
              )}
            </td>
            <td className="px-3 py-2 text-ink whitespace-nowrap">
              <span className="inline-flex items-center gap-2">
                <span className={`w-2 h-2 rounded-full shrink-0 ${TIER_BG_CLASS[worker.tier]}`} />
                {TIER_LABELS[worker.tier]}
              </span>{' '}
              <OnTrackHint tier={worker.tier} onTrack={worker.onTrack} />
            </td>
            <td className="px-3 py-2 text-ink text-right">{worker.score}</td>
            <td className="px-3 py-2 text-ink text-right">{metric(worker.callOuts)}</td>
            <td className="px-3 py-2 text-ink text-right">{metric(worker.chartingPct, '%')}</td>
            <td className="px-4 py-2 text-ink-mid">{BID_STATUS_LABELS[worker.bidStatus]}</td>
          </tr>
          {openWorkerId === worker.workerId && (
            <tr>
              <td colSpan={7} className="p-0 border-b border-marine/15">
                <AdminWorkerDetail api={api} clientId={clientId} workerId={worker.workerId} />
              </td>
            </tr>
          )}
          </Fragment>
        ))}
      </tbody>
    </table>
  )
}

/**
 * Every worker on one facility's roster, read-only - the same rows the
 * client's own dashboard table shows for that facility, sorted by name so a
 * particular person is easy to find.
 */
interface FacilityWorkersModalProps {
  api: AdminApi
  facility: AdminFacility
  /** Set when this opens on top of another dialog, naming it. */
  backLabel?: string
  onClose: () => void
}

export function FacilityWorkersModal({ api, facility, backLabel, onClose }: FacilityWorkersModalProps) {
  const workersQuery = useQuery({
    queryKey: ['admin', 'facility-workers', facility.clientId, facility.facilityId],
    queryFn: () => api.listFacilityWorkers(facility.clientId, facility.facilityId),
  })
  const workers = [...(workersQuery.data ?? [])].sort((a, b) => a.name.localeCompare(b.name) || a.workerId.localeCompare(b.workerId))

  return (
    <>
    <Modal
      eyebrow="Workers"
      title={facility.name}
      subtitle={
        workersQuery.isSuccess ? `${workers.length.toLocaleString()} ${workers.length === 1 ? 'worker' : 'workers'}` : undefined
      }
      backLabel={backLabel}
      onClose={onClose}
      footer={
        <Button icon={Check} autoFocus onClick={onClose}>
          Close
        </Button>
      }
    >
      {workersQuery.isLoading ? (
        <Skeleton lines={5} label="Loading workers" className="p-6" />
      ) : workersQuery.isError ? (
        <LoadFailure className="p-6" message="Failed to load this facility's workers." onRetry={() => void workersQuery.refetch()} retrying={workersQuery.isFetching} />
      ) : workers.length === 0 ? (
        <div className="p-6 text-sm text-ink-mid">
          <span className="font-bold text-ink">No workers set up.</span> {facility.name} has nobody on its roster yet. The client's operator adds workers from their
          dashboard; a whole roster can be imported as a file from the client's panel.
        </div>
      ) : (
        <WorkersTable api={api} clientId={facility.clientId} workers={workers} />
      )}
    </Modal>
    </>
  )
}
